import type { ReactNode } from "react";
import { cn } from "../lib/utils";
import { type BobineVue, couleurCss, remplissage } from "./bobines-textes";

/**
 * Les briques communes aux cartes du module Bobines, aux cotes des maquettes
 * `Bobines*.dc.html` : carte a 20 px de marge, pastille et jauge de couleur, encadre jaune.
 * Elles reprennent les classes des cadres sans les importer : un module n'importe jamais les
 * fichiers d'un autre.
 */

export const CARTE =
  "min-w-0 rounded-lg border border-border bg-card p-4 text-card-foreground md:p-5";

/** L'encadre jaune des avertissements (`--avertissement`), texte en `--foreground`. */
export const ALERTE_JAUNE =
  "flex flex-col gap-1.5 rounded-md border border-avertissement/60 bg-avertissement/15 px-3.5 py-3 text-sm";

/** Un anneau de focus visible, pose sur tous les controles faits main du module. */
export const FOCUS =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card";

/** Une commande du PC, dans le texte. */
export function Commande({ children }: { children: string }) {
  return (
    <code className="rounded-md border border-border bg-muted px-1.5 py-0.5 font-mono text-[12.5px] text-foreground">
      {children}
    </code>
  );
}

/** Le texte long, et sous `md` le texte court de la maquette du telephone. */
export function LongOuCourt({ long, court }: { long: ReactNode; court: ReactNode }) {
  return (
    <>
      <span className="hidden md:inline">{long}</span>
      <span className="md:hidden">{court}</span>
    </>
  );
}

/**
 * La pastille d'une couleur de filament. Son contour suit le texte (`--foreground`, a 40 %) :
 * un noir reste visible en theme sombre, un blanc en theme clair. Decorative : le nom de la
 * couleur est toujours ecrit a cote.
 */
export function Pastille({
  hex,
  petite = false,
  className,
}: {
  hex: string;
  petite?: boolean;
  className?: string;
}) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        "inline-block flex-none rounded-full border border-foreground/40",
        petite ? "size-3" : "size-5",
        className,
      )}
      style={{ backgroundColor: couleurCss(hex) }}
    />
  );
}

/**
 * La jauge d'une bobine, remplie de sa couleur a hauteur de ce qui reste. Contour ambre a
 * 150 g ou moins (presque vide), pointilles pour une finie ou pour une reference qui n'a plus
 * de bobine (`bobine` absente). Decorative : l'etat est ecrit en toutes lettres a cote.
 */
export function Jauge({
  bobine,
  hex,
  petite = false,
}: {
  bobine: Pick<BobineVue, "restant_g" | "depart_g" | "etat"> | null;
  hex: string;
  petite?: boolean;
}) {
  const part = bobine === null ? 0 : remplissage(bobine);
  const pointillee = bobine === null || bobine.etat === "finie";
  return (
    <span
      aria-hidden="true"
      data-etat={bobine?.etat ?? "aucune"}
      className={cn(
        "relative inline-block flex-none overflow-hidden rounded-[3px] border",
        petite ? "h-5 w-2.5" : "h-6 w-3",
        pointillee
          ? "border-dashed border-muted-foreground/50 bg-transparent"
          : "border-muted-foreground/60 bg-muted",
        bobine?.etat === "presque_vide" && "border-racheter ring-1 ring-racheter/30",
      )}
    >
      {part > 0 ? (
        <span
          className="absolute inset-x-0 bottom-0 border-t border-foreground/25"
          style={{ height: `${part}%`, backgroundColor: couleurCss(hex) }}
        />
      ) : null}
    </span>
  );
}

/** Le chevron d'un element repliable : a droite ferme, vers le bas ouvert. */
export function Chevron({ ouvert }: { ouvert: boolean }) {
  return (
    <svg
      aria-hidden="true"
      className={cn(
        "size-3 flex-none text-muted-foreground transition-transform",
        ouvert && "rotate-90",
      )}
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2.5"
      viewBox="0 0 12 12"
    >
      <path d="M4.5 2.5 8 6l-3.5 3.5" />
    </svg>
  );
}
